import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import SendReminderButton from "../components/SendReminderButton";
import {
  ChevronLeft,
  CalendarDays,
  MapPin,
  Trophy,
  AlertTriangle,
  Loader2,
  Users,
  BarChart2,
  Check,
  X,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type MatchInfo = {
  id: string;
  date: string;
  opponent: string | null;
  score: string | null;
  victory: boolean | null;
  location: string | null;
  team_id: string | null;
  team_name: string;
};

type PlayerStat = {
  id: string;
  player_number: number;
  player_name: string;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
  personal_fouls: number;
  is_opponent: boolean;
};

type AvailabilityEntry = {
  user_id: string;
  status: "available" | "unavailable";
  display_name: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtPct(made: number, attempted: number) {
  if (attempted === 0) return "–";
  return ((made / attempted) * 100).toFixed(0) + "%";
}

function teamTotals(stats: PlayerStat[]) {
  return stats.reduce(
    (acc, s) => ({
      points: acc.points + (s.points || 0),
      rebounds: acc.rebounds + (s.rebounds || 0),
      assists: acc.assists + (s.assists || 0),
      steals: acc.steals + (s.steals || 0),
      blocks: acc.blocks + (s.blocks || 0),
      turnovers: acc.turnovers + (s.turnovers || 0),
      shots_made: acc.shots_made + (s.shots_made || 0),
      shots_attempted: acc.shots_attempted + (s.shots_attempted || 0),
      ft_made: acc.ft_made + (s.ft_made || 0),
      ft_attempted: acc.ft_attempted + (s.ft_attempted || 0),
      three_made: acc.three_made + (s.three_made || 0),
      three_attempted: acc.three_attempted + (s.three_attempted || 0),
    }),
    {
      points: 0,
      rebounds: 0,
      assists: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      shots_made: 0,
      shots_attempted: 0,
      ft_made: 0,
      ft_attempted: 0,
      three_made: 0,
      three_attempted: 0,
    }
  );
}

// ─── Stat row ─────────────────────────────────────────────────────────────────

function StatRow({ stat }: { stat: PlayerStat }) {
  return (
    <tr className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
      <td className="px-3 py-2.5 text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
        <span className="inline-flex items-center gap-2">
          {stat.player_number != null && (
            <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right">
              {stat.player_number}
            </span>
          )}
          {stat.player_name}
        </span>
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
        {stat.position || "–"}
      </td>

      <td className="px-3 py-2.5 text-sm font-bold text-gray-900 dark:text-gray-100 text-center">
        {stat.points || 0}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
        {stat.rebounds || 0}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
        {stat.assists || 0}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
        {stat.steals || 0}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
        {stat.blocks || 0}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
        {stat.turnovers || 0}
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
        {stat.shots_made || 0}/{stat.shots_attempted || 0}
        <span className="text-gray-400 dark:text-gray-600 ml-1">
          ({fmtPct(stat.shots_made || 0, stat.shots_attempted || 0)})
        </span>
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
        {stat.three_made || 0}/{stat.three_attempted || 0}
        <span className="text-gray-400 dark:text-gray-600 ml-1">
          ({fmtPct(stat.three_made || 0, stat.three_attempted || 0)})
        </span>
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
        {stat.ft_made || 0}/{stat.ft_attempted || 0}
        <span className="text-gray-400 dark:text-gray-600 ml-1">
          ({fmtPct(stat.ft_made || 0, stat.ft_attempted || 0)})
        </span>
      </td>
    </tr>
  );
}

// ─── Availability chip ────────────────────────────────────────────────────────

function AvailabilityChip({
  name,
  status,
}: {
  name: string;
  status: "available" | "unavailable";
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
        status === "available"
          ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300"
          : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300"
      )}
    >
      {status === "available" ? (
        <Check className="h-3 w-3 shrink-0" />
      ) : (
        <X className="h-3 w-3 shrink-0" />
      )}
      {name}
    </span>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClubMatchDetails() {
  const { clubId, matchId, id } = useParams<{
    clubId: string;
    matchId?: string;
    id?: string;
  }>();

  const resolvedMatchId = matchId ?? id;

  const [match, setMatch] = useState<MatchInfo | null>(null);
  const [stats, setStats] = useState<PlayerStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [availabilities, setAvailabilities] = useState<AvailabilityEntry[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [savingAvail, setSavingAvail] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const myAvailability = useMemo(
    () => availabilities.find((a) => a.user_id === currentUserId)?.status ?? null,
    [availabilities, currentUserId]
  );

  const available = useMemo(
    () => availabilities.filter((a) => a.status === "available"),
    [availabilities]
  );

  const unavailable = useMemo(
    () => availabilities.filter((a) => a.status === "unavailable"),
    [availabilities]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (!resolvedMatchId) {
          throw new Error("ID du match manquant dans l'URL.");
        }

        const {
          data: { user },
        } = await supabase.auth.getUser();

        const uid = user?.id ?? null;

        const [matchRes, statsRes, availRes] = await Promise.all([
          supabase
            .from("matches")
            .select("id, date, opponent, score, victory, location, team_id")
            .eq("id", resolvedMatchId)
            .maybeSingle(),

          supabase
            .from("match_stats")
            .select("*")
            .eq("match_id", resolvedMatchId)
            .order("points", { ascending: false }),

          supabase
            .from("match_availability")
            .select("user_id, status")
            .eq("match_id", resolvedMatchId),
        ]);

        console.log("ClubMatchDetails params:", {
          clubId,
          matchId,
          id,
          resolvedMatchId,
        });
        console.log("ClubMatchDetails matchRes:", matchRes);
        console.log("ClubMatchDetails statsRes:", statsRes);
        console.log("ClubMatchDetails availRes:", availRes);

        if (matchRes.error) throw matchRes.error;
        if (statsRes.error) throw statsRes.error;
        if (availRes.error) throw availRes.error;

        if (!matchRes.data) {
          throw new Error(
            "Match introuvable. Vérifie que l'id du match existe et que l'utilisateur a les droits de lecture."
          );
        }

        const raw = matchRes.data as {
          id: string;
          date: string;
          opponent: string | null;
          score: string | null;
          victory: boolean | null;
          location: string | null;
          team_id: string | null;
        };

        let teamName = "Équipe";

        if (raw.team_id) {
          const { data: teamData, error: teamError } = await supabase
            .from("teams")
            .select("id, team_name, club_id")
            .eq("id", raw.team_id)
            .maybeSingle();

          console.log("ClubMatchDetails teamData:", teamData);
          console.log("ClubMatchDetails teamError:", teamError);

          if (teamError) throw teamError;

          if (clubId && teamData?.club_id && teamData.club_id !== clubId) {
            throw new Error("Ce match n'appartient pas à ce club.");
          }

          teamName = teamData?.team_name?.trim() || "Équipe";
        }

        let editable = false;
        if (uid && raw.team_id) {
          const [teamRoleRes, clubRoleRes] = await Promise.all([
            supabase
              .from("team_members")
              .select("role")
              .eq("team_id", raw.team_id)
              .eq("user_id", uid)
              .maybeSingle(),
            clubId
              ? supabase
                  .from("club_members")
                  .select("role")
                  .eq("club_id", clubId)
                  .eq("user_id", uid)
                  .maybeSingle()
              : Promise.resolve({ data: null as { role: string } | null }),
          ]);

          const teamRole = teamRoleRes.data?.role ?? null;
          const clubRole = clubRoleRes.data?.role ?? null;
          editable =
            ["dirigeant", "cadre_technique"].includes(clubRole ?? "") ||
            ["owner", "coach", "assistant"].includes(teamRole ?? "");
        }

        const availRows = (availRes.data ?? []) as {
          user_id: string;
          status: string;
        }[];

        const userIds = availRows.map((r) => r.user_id).filter(Boolean);

        const profileMap = new Map<string, string>();

        if (userIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from("user_profiles")
            .select("id, full_name, pseudo, public_use_pseudo")
            .in("id", userIds);

          if (profilesError) throw profilesError;

          for (const p of profiles ?? []) {
            const name =
              p.public_use_pseudo && p.pseudo
                ? p.pseudo
                : p.full_name?.trim() || "Membre";

            profileMap.set(p.id, name);
          }
        }

        let myName = "Moi";

        if (uid) {
          const { data: myProfile, error: myProfileError } = await supabase
            .from("user_profiles")
            .select("full_name, pseudo, public_use_pseudo")
            .eq("id", uid)
            .maybeSingle();

          if (myProfileError) throw myProfileError;

          if (myProfile) {
            myName =
              myProfile.public_use_pseudo && myProfile.pseudo
                ? myProfile.pseudo
                : myProfile.full_name?.trim() || "Moi";
          }
        }

        if (!cancelled) {
          setMatch({
            id: raw.id,
            date: raw.date,
            opponent: raw.opponent,
            score: raw.score,
            victory: raw.victory,
            location: raw.location,
            team_id: raw.team_id,
            team_name: teamName,
          });

          setStats((statsRes.data ?? []) as PlayerStat[]);
          setCurrentUserId(uid);
          setCurrentUserName(myName);
          setCanEdit(editable);

          setAvailabilities(
            availRows.map((r) => ({
              user_id: r.user_id,
              status: r.status as "available" | "unavailable",
              display_name: profileMap.get(r.user_id) ?? "Membre",
            }))
          );
        }
      } catch (err: any) {
        console.error("Erreur ClubMatchDetails:", err);

        if (!cancelled) {
          setError(err?.message || "Impossible de charger le match.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [resolvedMatchId, clubId, matchId, id]);

  const handleSetAvailability = useCallback(
    async (status: "available" | "unavailable") => {
      if (!resolvedMatchId || !currentUserId) return;

      setSavingAvail(true);

      try {
        if (myAvailability === status) {
          const { error } = await supabase
            .from("match_availability")
            .delete()
            .eq("match_id", resolvedMatchId)
            .eq("user_id", currentUserId);

          if (error) throw error;

          setAvailabilities((prev) =>
            prev.filter((a) => a.user_id !== currentUserId)
          );
        } else {
          const { error } = await supabase
            .from("match_availability")
            .upsert(
              {
                match_id: resolvedMatchId,
                user_id: currentUserId,
                status,
              },
              { onConflict: "match_id,user_id" }
            );

          if (error) throw error;

          setAvailabilities((prev) => [
            ...prev.filter((a) => a.user_id !== currentUserId),
            {
              user_id: currentUserId,
              status,
              display_name: currentUserName,
            },
          ]);
        }
      } catch (err) {
        console.error("Erreur disponibilité match:", err);
      } finally {
        setSavingAvail(false);
      }
    },
    [resolvedMatchId, currentUserId, myAvailability, currentUserName]
  );

  const myStats = useMemo(
    () => stats.filter((s) => !s.is_opponent),
    [stats]
  );

  const oppStats = useMemo(
    () => stats.filter((s) => s.is_opponent),
    [stats]
  );

  const myTotals = useMemo(() => teamTotals(myStats), [myStats]);
  const oppTotals = useMemo(() => teamTotals(oppStats), [oppStats]);

  const upcoming = match ? new Date(match.date) >= new Date() : false;
  const hasResult = Boolean(match && !upcoming && match.score);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement du match…
        </div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="space-y-4">
        <Link
          to={`/club/${clubId}/matches`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour aux matchs
        </Link>

        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error || "Match introuvable."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/club/${clubId}/matches`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour aux matchs
        </Link>

        {canEdit && upcoming && resolvedMatchId && (
          <SendReminderButton type="match" eventId={resolvedMatchId} />
        )}
      </div>

      {/* Score hero */}
      <div
        className={cx(
          "rounded-2xl border p-6",
          upcoming
            ? "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800"
            : match.victory === true
            ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900/40"
            : match.victory === false
            ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/40"
            : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800"
        )}
      >
        <div className="flex items-center justify-center gap-4 text-center">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Notre équipe
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {match.team_name}
            </div>
          </div>

          <div className="shrink-0 text-center">
            {hasResult && match.score ? (
              <div className="text-4xl font-black tracking-tight text-gray-900 dark:text-gray-100 font-mono">
                {match.score}
              </div>
            ) : (
              <div className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                vs
              </div>
            )}

            {!upcoming && match.victory !== null && (
              <div
                className={cx(
                  "mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full",
                  match.victory
                    ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                    : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                )}
              >
                <Trophy className="h-3.5 w-3.5" />
                {match.victory ? "Victoire" : "Défaite"}
              </div>
            )}

            {upcoming && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                À venir
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Adversaire
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {match.opponent || "Adversaire inconnu"}
            </div>
          </div>
        </div>
      </div>

      {/* Disponibilités */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Disponibilités
          </span>

          {availabilities.length > 0 && (
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
              {available.length} dispo · {unavailable.length} indispo
            </span>
          )}
        </div>

        {currentUserId && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 shrink-0">
              Êtes-vous disponible ?
            </span>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={savingAvail}
                onClick={() => handleSetAvailability("available")}
                className={cx(
                  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition disabled:opacity-50",
                  myAvailability === "available"
                    ? "bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                    : "bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-green-900/20 dark:hover:text-green-300 border border-gray-200 dark:border-gray-700"
                )}
              >
                {savingAvail && myAvailability !== "available" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Disponible
              </button>

              <button
                type="button"
                disabled={savingAvail}
                onClick={() => handleSetAvailability("unavailable")}
                className={cx(
                  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition disabled:opacity-50",
                  myAvailability === "unavailable"
                    ? "bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                    : "bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-red-900/20 dark:hover:text-red-300 border border-gray-200 dark:border-gray-700"
                )}
              >
                {savingAvail && myAvailability !== "unavailable" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                Indisponible
              </button>
            </div>

            {myAvailability && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Cliquez à nouveau pour retirer votre réponse.
              </span>
            )}
          </div>
        )}

        {availabilities.length === 0 ? (
          <div className="text-sm text-gray-400 dark:text-gray-500">
            Aucune réponse pour le moment.
          </div>
        ) : (
          <div className="space-y-3">
            {available.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-2">
                  Disponibles ({available.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {available.map((a) => (
                    <AvailabilityChip
                      key={a.user_id}
                      name={a.display_name}
                      status="available"
                    />
                  ))}
                </div>
              </div>
            )}

            {unavailable.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">
                  Indisponibles ({unavailable.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {unavailable.map((a) => (
                    <AvailabilityChip
                      key={a.user_id}
                      name={a.display_name}
                      status="unavailable"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
            <CalendarDays className="h-4 w-4" />
            Date &amp; heure
          </div>
          <div className="text-sm text-gray-900 dark:text-gray-100 capitalize">
            {formatDateTime(match.date)}
          </div>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
            <MapPin className="h-4 w-4" />
            Lieu
          </div>

          {match.location ? (
            <span
              className={cx(
                "inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full",
                match.location === "domicile"
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  : "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
              )}
            >
              <MapPin className="h-3.5 w-3.5" />
              {match.location === "domicile" ? "Domicile" : "Extérieur"}
            </span>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
            <Users className="h-4 w-4" />
            Équipe
          </div>
          <div className="text-sm text-gray-900 dark:text-gray-100">
            {match.team_name}
          </div>
        </div>
      </div>

      {/* Stats */}
      {myStats.length > 0 ? (
        <div className="space-y-4">
          {oppTotals.points > 0 && (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                <BarChart2 className="h-4 w-4 text-gray-500" />
                Résumé collectif
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Points", my: myTotals.points, opp: oppTotals.points },
                  { label: "Rebonds", my: myTotals.rebounds, opp: oppTotals.rebounds },
                  { label: "Passes", my: myTotals.assists, opp: oppTotals.assists },
                  { label: "Interceptions", my: myTotals.steals, opp: oppTotals.steals },
                  { label: "Contres", my: myTotals.blocks, opp: oppTotals.blocks },
                  {
                    label: "Pertes de balle",
                    my: myTotals.turnovers,
                    opp: oppTotals.turnovers,
                  },
                ].map((row) => {
                  const myWins =
                    row.label === "Pertes de balle"
                      ? row.my <= row.opp
                      : row.my >= row.opp;

                  return (
                    <div
                      key={row.label}
                      className="flex items-center gap-2 rounded-xl bg-gray-50 dark:bg-gray-800/50 px-3 py-2"
                    >
                      <span
                        className={cx(
                          "text-sm font-bold w-8 text-right",
                          myWins
                            ? "text-green-700 dark:text-green-400"
                            : "text-gray-900 dark:text-gray-100"
                        )}
                      >
                        {row.my}
                      </span>

                      <span className="flex-1 text-xs text-center text-gray-500 dark:text-gray-400">
                        {row.label}
                      </span>

                      <span
                        className={cx(
                          "text-sm font-bold w-8 text-left",
                          !myWins
                            ? "text-green-700 dark:text-green-400"
                            : "text-gray-900 dark:text-gray-100"
                        )}
                      >
                        {row.opp}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-2 flex justify-between text-xs text-gray-400 dark:text-gray-500 px-3">
                <span>{match.team_name}</span>
                <span>{match.opponent || "Adversaire"}</span>
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Stats individuelles — {match.team_name}
              </span>
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                {myStats.length} joueur{myStats.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[700px]">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/60">
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      Joueur
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      Pos.
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-900 dark:text-gray-100 text-center">
                      PTS
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      REB
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      PAS
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      INT
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      CTR
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      PB
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      2pts
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      3pts
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                      LF
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {myStats.map((s) => (
                    <StatRow key={s.id} stat={s} />
                  ))}

                  <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-semibold">
                    <td
                      className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                      colSpan={2}
                    >
                      Total équipe
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-gray-900 dark:text-gray-100 text-center">
                      {myTotals.points}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
                      {myTotals.rebounds}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
                      {myTotals.assists}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
                      {myTotals.steals}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
                      {myTotals.blocks}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-center">
                      {myTotals.turnovers}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {myTotals.shots_made}/{myTotals.shots_attempted}
                      <span className="text-gray-400 dark:text-gray-600 ml-1">
                        ({fmtPct(myTotals.shots_made, myTotals.shots_attempted)})
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {myTotals.three_made}/{myTotals.three_attempted}
                      <span className="text-gray-400 dark:text-gray-600 ml-1">
                        ({fmtPct(myTotals.three_made, myTotals.three_attempted)})
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {myTotals.ft_made}/{myTotals.ft_attempted}
                      <span className="text-gray-400 dark:text-gray-600 ml-1">
                        ({fmtPct(myTotals.ft_made, myTotals.ft_attempted)})
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        !upcoming && (
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
            <BarChart2 className="h-8 w-8" />
            <div className="text-sm">
              Aucune statistique enregistrée pour ce match.
            </div>

            {match.team_id && (
              <Link
                to={`/club/${clubId}/team/${match.team_id}/stats`}
                className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
              >
                Voir les stats de l'équipe →
              </Link>
            )}
          </div>
        )
      )}
    </div>
  );
}